import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import nurseryQuarantineService from '@/services/nurseryQuarantine';
import { useOrgTimezone, formatDateTimeInTz } from '@/utils/timezone';
import { QUARANTINE_METHOD_LABEL } from '@/types/nurseryQuarantine';
import type { SprayQuarantine } from '@/types/nurseryQuarantine';
import QuarantineOrderSignoffs from '@/components/nursery/despatch/QuarantineOrderSignoffs';

/**
 * On a spray that carries out an ICA quarantine treatment: which load and
 * protocol it is for, where it is done, and each customer's order. Finishing
 * the spray signs every order off as treated (Rob, 2026-10-19); someone else
 * then validates the treatment on the load page.
 *
 * Renders nothing for any other spray, or if the check fails: it must never
 * get in the way of running a spray.
 */
function SprayQuarantineCard({ sprayId, sprayStatus }: { sprayId: number; sprayStatus: string }) {
  const tz = useOrgTimezone();
  const [data, setData] = useState<SprayQuarantine | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await nurseryQuarantineService.getSprayQuarantine(sprayId);
      if (res.success) setData(res.data ?? null);
    } catch { /* not a quarantine spray, or no nursery access: show nothing */ }
  }, [sprayId]);

  useEffect(() => { load(); }, [load, sprayStatus]);

  if (!data) return null;

  const toSign = data.orders.filter((o) => !o.gone);
  const signed = toSign.filter((o) => o.signed_at).length;
  const allSigned = toSign.length > 0 && signed === toSign.length;
  const method = data.method && data.method !== 'all' ? QUARANTINE_METHOD_LABEL[data.method] : null;
  const note = data.validated_at
    ? `Validated by ${data.validated_by_name || 'someone'} ${formatDateTimeInTz(data.validated_at, tz)}.`
    : data.state === 'cancelled'
      ? 'The spray was cancelled, so nothing was treated.'
      : allSigned
        ? null
        : "Treat every customer's order below. Finishing the spray signs them all off as treated, and someone other than you then validates the treatment.";

  return (
    <section className="bg-white border border-neutral-200 rounded-xl p-5 space-y-3" aria-labelledby="spray-quarantine">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 id="spray-quarantine" className="flex items-center gap-2 text-sm font-semibold text-neutral-600 uppercase tracking-wide">
          <ShieldCheck className="h-4 w-4" /> Quarantine treatment
        </h3>
        {data.order_signoff && (
          <span className="text-sm text-neutral-600">{`${signed} of ${toSign.length} order${toSign.length === 1 ? '' : 's'} signed off`}</span>
        )}
      </div>
      <p className="text-sm text-neutral-700 break-words">
        {`${data.code} · ${data.name}${method ? ` · ${method}` : ''}, for load `}
        <Link to={`/nursery/loads/${data.load_id}`} className="underline font-medium">{data.load_number}</Link>
        {data.area_name ? `. Treated at ${data.area_name}.` : '.'}
      </p>

      {data.order_signoff ? (
        <>
          {note && <p className="text-xs text-neutral-500">{note}</p>}
          <QuarantineOrderSignoffs rows={data.orders} tz={tz} />
          {allSigned && !data.validated_at && (
            <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
              {sprayStatus === 'completed'
                ? 'Every order is signed off. Someone other than whoever applied it validates the treatment on the load page.'
                : 'Every order is signed off. Complete this spray: the treatment cannot be validated, and the load cannot go, until it is.'}
            </p>
          )}
        </>
      ) : (
        <p className="text-xs text-neutral-500">Made before orders were signed off one by one: it is validated on the load page.</p>
      )}
    </section>
  );
}

export default SprayQuarantineCard;
